import { defineStore } from 'pinia'
import adminRequest, { clearAdminAuthStorage } from '@/api/adminRequest'
import type { AdminActionCode } from '@/config/adminActions'

export interface AdminInfo {
  id: string
  username: string
  name: string
  role: 'super_admin' | 'ops' | 'finance' | 'support'
  status?: 'active' | 'disabled'
}

interface AdminAuthState {
  token: string
  admin: AdminInfo | null
  actions: string[]
  profileLoaded: boolean
}

function readToken(): string {
  return localStorage.getItem('admin_access_token')
    ?? sessionStorage.getItem('admin_access_token')
    ?? ''
}

export const useAdminAuthStore = defineStore('adminAuth', {
  state: (): AdminAuthState => ({
    token: readToken(),
    admin: null,
    actions: [],
    profileLoaded: false,
  }),

  getters: {
    isLoggedIn: (state) => !!state.token,
    hasAction: (state) => (code: AdminActionCode | string) => (
      state.actions.includes('*') || state.actions.includes(code)
    ),
  },

  actions: {
    async login(username: string, password: string) {
      const data: any = await adminRequest.post('/auth/login', { username, password })
      if (!data?.access_token) throw new Error('登录失败：未返回 token')

      clearAdminAuthStorage()
      this.token = data.access_token
      this.admin = data.admin || null
      this.actions = Array.isArray(data.actions) ? data.actions : []
      this.profileLoaded = !!data.admin && Array.isArray(data.actions)
      localStorage.setItem('admin_access_token', this.token)
      return data
    },

    async fetchMe() {
      const data: any = await adminRequest.get('/auth/me')
      this.admin = data?.admin || null
      this.actions = Array.isArray(data?.actions) ? data.actions : []
      this.profileLoaded = true
      return data
    },

    async logout() {
      try {
        if (this.token) await adminRequest.post('/auth/logout')
      } catch (error: any) {
        if (error?.status !== 401) throw error
      }
      this.clearSession()
    },

    clearSession() {
      clearAdminAuthStorage()
      this.token = ''
      this.admin = null
      this.actions = []
      this.profileLoaded = false
    },
  },
})
